import React from 'react';
import { Box, Checkbox, FormControlLabel, Stack, TextField } from '@mui/material';

export interface FieldWithPropertiesProps {
  /** The field control rendered on the left. */
  field: React.ReactNode;
  visible: boolean;
  required: boolean;
  label?: string;
  /** Hide the properties panel entirely (e.g. when no field is configurable). */
  showProperties?: boolean;
  /**
   * Hide the Visible/Required checkboxes (but keep the Label field). Used when
   * the backend mandates the field for the campaign type, so toggling makes no
   * sense.
   */
  hideToggles?: boolean;
  onVisibleChange: (value: boolean) => void;
  onRequiredChange: (value: boolean) => void;
  onLabelChange: (value: string) => void;
}

/**
 * Renders a configurable field next to its Visible / Required / Label property
 * panel. Replaces the ~40-line block that was duplicated four times in the
 * original ConfigurationSection (groups, keywords, appointment, contact date).
 */
export const FieldWithProperties: React.FC<FieldWithPropertiesProps> = ({
  field,
  visible,
  required,
  label,
  showProperties = true,
  hideToggles = false,
  onVisibleChange,
  onRequiredChange,
  onLabelChange,
}) => (
  <Box>
    <Box display="flex" alignItems="flex-start" gap={2}>
      <Box flex={1}>{field}</Box>
      {showProperties && (
        <Stack
          gap={1}
          p={1}
          mb={1}
          border={1}
          borderColor="divider"
          borderRadius={1}
          flexShrink={0}
          minWidth={250}
        >
          {!hideToggles && (
            <Box display="flex" gap={2} alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={visible ?? false}
                    onChange={e => onVisibleChange(e.target.checked)}
                    inputProps={{ 'aria-label': 'Visible' }}
                  />
                }
                label="Visible"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={required ?? false}
                    onChange={e => onRequiredChange(e.target.checked)}
                    inputProps={{ 'aria-label': 'Required' }}
                  />
                }
                label="Required"
              />
            </Box>
          )}
          <TextField
            label="Label"
            value={label || ''}
            onChange={e => onLabelChange(e.target.value)}
            placeholder="Enter custom label"
            fullWidth
          />
        </Stack>
      )}
    </Box>
  </Box>
);
